import { useEffect, useState } from 'react';

import { generateRunAndReturnSnippet } from 'dashboard/components/GridLayoutRender/computed-properties/helpers';
import { ComputedProperty } from 'dashboard/lib/case-layouts/computed-properties';

export function useComputedProperty<T>(
  runCodeWithContext: (code: string) => Promise<unknown>,
  computedProperty?: ComputedProperty,
  fallback?: T | null,
): [T | null, Error | null] {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [value, setValue] = useState<T | null>(fallback);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!computedProperty) return;

    const returnType = computedProperty['return-type'];
    const isRequired = computedProperty['is-required'];

    switch (computedProperty['resolution-mechanism']['resolution-type']) {
      case 'custom-code': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const deserializedCode = atob(computedProperty['resolution-mechanism']['serialized-code']);

        runCodeWithContext(generateRunAndReturnSnippet(deserializedCode))
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          .then((returnValue: T) => {
            if (isRequired && typeof returnValue !== returnType) {
              throw new TypeError(
                `custom code must return a ${returnType} value, got ${typeof returnValue} instead.`,
              );
            } else if (
              !isRequired &&
              typeof returnValue !== 'undefined' &&
              typeof returnValue !== returnType
            ) {
              throw new TypeError(
                `custom code must return ${returnType} or undefined, got ${typeof returnValue} instead.`,
              );
            }

            setValue(returnValue);
          })
          .catch((err) => {
            console.error(`${deserializedCode} ${err.stack}`);
            setError(err);
          });
        break;
      }
      case 'template-literal': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const deserializedCode = atob(computedProperty['resolution-mechanism']['serialized-code']);

        let runCodeSnippet = '';
        // if there is one starting and ending brace AND there is no text around them, then evaluate as code, otherwise evaluate as a template literal
        const splitCode = deserializedCode.split(/{{|}}/);
        if (splitCode.length === 3 && splitCode[0] === '' && splitCode[2] === '') {
          runCodeSnippet = `return ${splitCode[1]}`;
        } else {
          const templateLiteral = deserializedCode.replace(/{{/g, '${').replace(/}}/g, '}');
          runCodeSnippet = `return \`${templateLiteral}\``;
        }

        // run and return javascript template
        runCodeWithContext(runCodeSnippet)
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          .then((returnValue: T) => {
            // let consumers handle undefined values by passing in a fallback
            // TODO(brandon): eventually make this consistent across custom code and template literals
            if (returnValue == null) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              setValue(fallback);
            } else {
              setValue(returnValue);
            }
          })
          .catch((err) => {
            console.error(err);
            setError(err);
            // TODO brandon, figure out the best way to handle errors for all these computed properties
          });
        break;
      }
      default: {
        // no action
      }
    }
  }, [computedProperty, runCodeWithContext, fallback]);

  return [value, error];
}
